<template>
  <div
    :class="componentClasses"
    @click="clickInput"
    @dragenter.prevent="dndEnter"
    @dragleave.prevent="dndLeave"
    @dragover.prevent="/* Needed to prevent browser from loading file */"
    @drop.prevent="dndDrop">

    <input
      type="file"
      ref="fileInput"
      :disabled="disabled"
      :multiple="multiple"
      @input="onInput">

    <slot v-if="value.length === 0">
      <div
        class="instructions"
        v-if="multiple">
        Drop files here or click to upload.
      </div>
      <div
        class="instructions"
        v-else>
        Drop file here or click to upload.
      </div>
    </slot>

    <ol
      class="file-list"
      v-else>
      <li
        @click.stop=""
        v-for="file in value">
        <img
          :src="file.objectUrl"
          v-if="file.objectUrl">
        <div :class="{ 'auto-hide': !!file.objectUrl }">
          <div class="file-info">
            <div class="file-size">{{formatFileSize(file.size)}}</div>
            <div class="file-name">{{file.name}}</div>
            <button
              type="button"
              :disabled="disabled"
              @click.stop="removeFiles([file])">
              Remove
            </button>
          </div>
        </div>
      </li>
    </ol>

  </div>
</template>

<script>
  import ByteUtils from '../../../packages/byte-utils';

  export default {
    name: 'file-select',

    props: {
      multiple: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      value: {
        type: Array,
        // List of files
        // Supports @input events to be used with v-model
      },
    },

    data() {
      return {
        // We need to be able to tell whether files are currently held over the
        // drop area. To do this, we can use the `dragenter`/`dragleave` events.
        // However, these events will trigger for every element within the
        // component, not just the outer-most element. Since these events bubble,
        // we can simply keep a running count of enter/leave events, and whenever
        // the count is above zero, then files are held within the drop zone.
        dndEnterCount: 0,
      };
    },

    beforeDestroy() {
      this.removeAllFiles();
    },

    methods: {
      formatFileSize(size) {
         return ByteUtils.autoFormatBytes(size);
      },

      // addFiles() is used instead of addFile() since many input events
      // back-to-back don't seem to behave correctly
      addFiles(files) {
        files.forEach(file => {
          if (file.type.indexOf('image') === 0) {
            file.objectUrl = URL.createObjectURL(file);
          }
        });
        this.$emit('input', this.value.concat(files));
      },

      removeFiles(files) {
        files.forEach(file => {
          if (file.objectUrl) {
            URL.revokeObjectURL(file.objectUrl);
            delete file.objectUrl;
          }
        });
        this.$emit('input', this.value.filter(f => files.indexOf(f) === -1));
      },

      removeAllFiles() {
        this.removeFiles(this.value.slice());
      },

      clickInput() {
        if (this.disabled) {
          return;
        }
        this.$refs.fileInput.click();
      },

      onInput() {
        const newFiles = Array.from(this.$refs.fileInput.files);

        if (this.multiple) {
          this.addFiles(newFiles);
        } else {
          this.removeAllFiles();
          this.addFiles([newFiles[0]]);
        }
      },

      dndEnter(ev) {
        this.dndEnterCount++;
      },

      dndLeave(ev) {
        this.dndEnterCount--;
      },

      dndDrop(ev) {
        if (this.disabled) {
          return;
        }
        if (ev.dataTransfer.files.length === 0) {
          return;
        }

        const newFiles = Array.from(ev.dataTransfer.files);

        if (this.multiple) {
          this.addFiles(newFiles);
        } else {
          this.removeAllFiles();
          this.addFiles([newFiles[0]]);
        }
      },
    },

    computed: {
      filesOver() {
        return this.dndEnterCount > 0;
      },

      componentClasses() {
        return {
          'file-select': true,
          'files-over': this.filesOver,
          'disabled': this.disabled,
        };
      },
    },
  }
</script>

<style lang="scss" scoped>
  .file-select {
    border: 1px solid #555;
    background-color: #1a1a1a;
    cursor: pointer;

    &.files-over {
      .instructions {
        opacity: 0.6;
      }
    }

    &.disabled {
      cursor: default;
    }
  }

  input[type="file"] {
    display: none;
  }

  .instructions {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 120px;
    padding: 10px;
  }

  .file-list {
    list-style-type: none;
    padding: 10px;
    margin: 0;
    text-align: left;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-gap: 10px;

    li {
      position: relative;
      cursor: default;
      &:hover .auto-hide {
        display: block;
      }
      &::before {
        content: '';
        display: inline-block;
        padding-top: 100%;
        float: left;
      }
      &::after {
        content: '';
        display: table;
        clear: both;
      }
    }

    .auto-hide {
      display: none;
    }

    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .file-info {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10;
      &:hover {
        background-color: rgba(0, 0, 0, 0.5);
      }

      .file-size,
      .file-name {
        background-color: #333;
        border: 1px solid #ccc;
        color: #ccc;
        display: inline-block;
        padding: 2px 5px;
        max-width: calc(100% - 20px);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        &:hover {
          max-width: none;
        }
      }

      .file-size {
        position: absolute;
        top: 10px;
        left: 50%;
        transform: translateX(-50%);
      }

      .file-name {
        font-size: 12px;
        position: absolute;
        top: calc(50% - 3px);
        left: 50%;
        transform: translate(-50%, -50%);
      }

      button {
        background-color: #333;
        border: 2px solid white;
        color: white;
        position: absolute;
        left: 10px;
        bottom: 10px;
        width: 100px;
        max-width: calc(100% - 20px);
        height: 30px;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// index.vue?003516c6